import React, { useContext, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { DashboardContext } from "../contexts/DashboardContext";

export default function HiringTrendChart() {
  const { employeesData } = useContext(DashboardContext);

  // Dynamically calculate Joined and Left for the last 6 months
  const dynamicHiringData = useMemo(() => {
    const data = employeesData || [];
    const result = [];
    const today = new Date();

    // 1. Generate an array of the last 6 months (e.g., ["Mar 2026", "Apr 2026", ...])
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthLabel = d.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      result.push({ month: monthLabel, Joined: 0, Left: 0 });
    }

    // 2. Loop through live backend data to populate the counts
    data.forEach((emp) => {
      // Calculate "Joined" using created_at
      if (emp.created_at) {
        const joinDate = new Date(emp.created_at);
        const joinMonth = joinDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        const targetMonth = result.find((m) => m.month === joinMonth);
        if (targetMonth) {
          targetMonth.Joined += 1;
        }
      }

      // Calculate "Left" by checking for inactive statuses
      const status = (emp.status || "").toLowerCase();
      if (["terminated", "resigned", "left", "inactive"].includes(status)) {
        // If they left, use updated_at (or deleted_at if your backend uses soft deletes)
        const leftDateStr = emp.deleted_at || emp.updated_at;
        if (leftDateStr) {
          const leftDate = new Date(leftDateStr);
          const leftMonth = leftDate.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
          const targetMonth = result.find((m) => m.month === leftMonth);
          if (targetMonth) {
            targetMonth.Left += 1;
          }
        }
      }
    });

    return result;
  }, [employeesData]);

  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col h-[340px]">
      <h3 className="text-sm font-bold text-slate-800 mb-2">
        Hiring Trend (Last 6 Months)
      </h3>
      <div className="flex-1 w-full mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={dynamicHiringData}
            margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f1f5f9"
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#64748b" }}
              dy={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#64748b" }}
            />
            <Tooltip />
            <Legend
              iconType="circle"
              wrapperStyle={{ fontSize: "11px", top: -30, right: 0 }}
              align="right"
              verticalAlign="top"
            />
            <Line
              type="monotone"
              dataKey="Joined"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Left"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
